import { useCallback, useState } from "react";
import { Button, Flex, ModalV2, TextInput } from "@jsq/ds/core";
import { SPACING } from "~/styles/variables";
import { getTemplateNameError } from "../../ReportTemplateEditor/templateNameValidation";

type UpdateTemplateNameModalProps = {
  isOpen: boolean;
  currentName: string;
  existingTemplateNames: Set<string>;
  onClose: () => void;
  onConfirm: (newName: string) => void;
  isUpdating: boolean;
};

const UpdateTemplateNameForm = ({
  currentName,
  existingTemplateNames,
  onClose,
  onConfirm,
  isUpdating,
}: Omit<UpdateTemplateNameModalProps, "isOpen">) => {
  const [name, setName] = useState(currentName);

  const errorMessage = getTemplateNameError(name, existingTemplateNames, currentName);

  const handleConfirm = useCallback(() => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== currentName) {
      onConfirm(trimmed);
    }
  }, [name, currentName, onConfirm]);

  const isDisabled =
    !name.trim() || name.trim() === currentName || !!errorMessage || isUpdating;

  return (
    <>
      <ModalV2.Body>
        <Flex flexDirection="column" gap={SPACING.MEDIUM}>
          <TextInput
            label="Template name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            isFullWidth
            errorMessage={errorMessage}
            data-testid="template-name-input"
          />
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end" gap={SPACING.SMALL}>
          <Button variant="neutral" onClick={onClose} disabled={isUpdating}>
            Exit
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isDisabled}
            isLoading={isUpdating}
            data-testid="update-template-name-button"
          >
            Update name
          </Button>
        </Flex>
      </ModalV2.Footer>
    </>
  );
};

export const UpdateTemplateNameModal = ({
  isOpen,
  currentName,
  existingTemplateNames,
  onClose,
  onConfirm,
  isUpdating,
}: UpdateTemplateNameModalProps) => (
  <ModalV2
    show={isOpen}
    onHide={onClose}
    size="small"
    title="Update template name"
    data-testid="update-template-name-modal"
    closeOnClickOutside={false}
  >
    {isOpen && (
      <UpdateTemplateNameForm
        currentName={currentName}
        existingTemplateNames={existingTemplateNames}
        onClose={onClose}
        onConfirm={onConfirm}
        isUpdating={isUpdating}
      />
    )}
  </ModalV2>
);
